import { EXCEPTION_CATEGORIES, METRIC_TYPE_OPTIONS } from './reconExceptionFilters';

const FILTER_INPUT = 'filter-input';

/**
 * Exception list with per-column filters, Apply, and Oracle element picker (datalist).
 */
export default function ExceptionTable({
  rows,
  filterOptions,
  filters,
  onFilterChange,
  onApply,
  onClearFilters,
  hasActiveFilters,
  hasPendingApply,
  loading,
  showCategory = true,
  emptyMessage = 'No rows match the current filters.',
}) {
  const set = (key, value) => onFilterChange(key, value);
  const elementListId = 'oracle-element-filter-list';

  const categoryCount = (name) =>
    filterOptions?.categories?.find((c) => c.name === name)?.count;

  const elements = filterOptions?.elements || [];

  return (
    <div className="space-y-2">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onApply();
        }}
        className="flex flex-wrap items-center justify-between gap-2"
      >
        <p className="text-xs text-slate-400">
          {hasPendingApply
            ? 'Press Apply to filter by person, amounts, and search.'
            : hasActiveFilters
              ? 'Filters applied.'
              : showCategory
                ? 'Filter any column below. Issue and Oracle element update immediately.'
                : 'Filter any column below. Oracle element updates immediately.'}
        </p>
        <div className="flex flex-wrap gap-2">
          <button type="submit" className="btn-primary py-1.5 text-sm" disabled={loading}>
            Apply filters
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="btn-secondary py-1.5 text-sm"
            >
              Clear all
            </button>
          )}
        </div>
      </form>
      <div className="card-panel overflow-x-auto">
        <table className="w-full min-w-[1020px] text-left text-xs">
          <thead>
            <tr className="border-b border-slate-700 bg-slate-800/80 text-slate-400">
              <th className="px-2 py-2 font-medium">Person</th>
              <th className="px-2 py-2 font-medium">Oracle element</th>
              <th className="px-2 py-2 font-medium">ADP wage type(s)</th>
              <th className="px-2 py-2 font-medium">Metric</th>
              {showCategory && <th className="px-2 py-2 font-medium">Issue</th>}
              <th className="px-2 py-2 text-right font-medium">Source</th>
              <th className="px-2 py-2 text-right font-medium">ADP</th>
              <th className="px-2 py-2 text-right font-medium">Diff</th>
            </tr>
            <tr className="border-b border-slate-700 bg-slate-800/50">
              <th className="px-2 py-1.5">
                <input
                  value={filters.person}
                  onChange={(e) => set('person', e.target.value)}
                  placeholder="Person #"
                  className={FILTER_INPUT}
                />
              </th>
              <th className="px-2 py-1.5">
                <input
                  list={elements.length ? elementListId : undefined}
                  value={filters.element}
                  onChange={(e) => set('element', e.target.value)}
                  placeholder={elements.length ? 'Pick or type element…' : 'Type element name…'}
                  title="Oracle element — pick from list or type to match"
                  className={`${FILTER_INPUT} min-w-[10rem]`}
                />
                {elements.length > 0 && (
                  <datalist id={elementListId}>
                    {elements.map((el) => (
                      <option key={el.name} value={el.name}>
                        {el.count.toLocaleString()} rows
                      </option>
                    ))}
                  </datalist>
                )}
              </th>
              <th className="px-2 py-1.5">
                <input
                  value={filters.wageType}
                  onChange={(e) => set('wageType', e.target.value)}
                  placeholder="Wage type"
                  className={FILTER_INPUT}
                />
              </th>
              <th className="px-2 py-1.5">
                <select
                  value={filters.metric || ''}
                  onChange={(e) => set('metric', e.target.value)}
                  className={FILTER_INPUT}
                  title="Dollars or hours — applies immediately"
                >
                  {METRIC_TYPE_OPTIONS.map((m) => (
                    <option key={m.value || 'all'} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </th>
              {showCategory && (
                <th className="px-2 py-1.5">
                  <select
                    value={filters.category}
                    onChange={(e) => set('category', e.target.value)}
                    className={`${FILTER_INPUT} min-w-[11rem] max-w-none`}
                    title="Issue type — applies immediately"
                  >
                    {EXCEPTION_CATEGORIES.map((c) => {
                      const n = c.value ? categoryCount(c.value) : filterOptions?.total;
                      return (
                        <option key={c.value || 'all'} value={c.value}>
                          {c.label}
                          {n != null ? ` (${n.toLocaleString()})` : ''}
                        </option>
                      );
                    })}
                  </select>
                </th>
              )}
              <th className="px-2 py-1.5">
                <input
                  value={filters.oracleAmount}
                  onChange={(e) => set('oracleAmount', e.target.value)}
                  placeholder=">1000"
                  title="Exact amount, or prefix with > or < (e.g. >0)"
                  className={`${FILTER_INPUT} text-right`}
                />
              </th>
              <th className="px-2 py-1.5">
                <input
                  value={filters.adpAmount}
                  onChange={(e) => set('adpAmount', e.target.value)}
                  placeholder="0"
                  title="Exact amount only for plain numbers (0 = exactly zero). Use >0 for positive."
                  className={`${FILTER_INPUT} text-right`}
                />
              </th>
              <th className="px-2 py-1.5">
                <input
                  value={filters.difference}
                  onChange={(e) => set('difference', e.target.value)}
                  placeholder=">0"
                  title="Exact amount, or prefix with > or <"
                  className={`${FILTER_INPUT} text-right`}
                />
              </th>
            </tr>
            <tr className="border-b border-slate-800 bg-slate-900/50">
              <th colSpan={showCategory ? 8 : 7} className="px-2 py-1.5">
                <input
                  value={filters.q}
                  onChange={(e) => set('q', e.target.value)}
                  placeholder="Search all columns — press Apply"
                  className="filter-input text-sm"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={showCategory ? 8 : 7} className="px-2 py-8 text-center text-slate-400">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((ex, i) => (
                <tr
                  key={`${ex.metricType}-${ex.personNumber}-${ex.oracleElementName}-${ex.category}-${i}`}
                  className="border-b border-slate-800 text-slate-300"
                >
                  <td className="px-2 py-1.5 font-mono">{ex.personNumber}</td>
                  <td className="px-2 py-1.5">{ex.oracleElementName}</td>
                  <td className="px-2 py-1.5 font-mono">
                    {ex.adpWageTypeCodes || ex.adpWageTypes || '—'}
                  </td>
                  <td className="px-2 py-1.5 capitalize">{ex.metricType || 'dollars'}</td>
                  {showCategory && <td className="px-2 py-1.5">{ex.category}</td>}
                  <td className="px-2 py-1.5 text-right font-mono">
                    {ex.metricType === 'hours'
                      ? formatHours(ex.g2Hours)
                      : formatMoney(ex.oracleAmount)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {ex.metricType === 'hours'
                      ? formatHours(ex.registerHours)
                      : formatMoney(ex.adpAmount)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">
                    {ex.metricType === 'hours' ? formatHours(ex.difference) : formatMoney(ex.difference)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {!filterOptions && (
        <p className="text-xs text-amber-700">
          Element list unavailable — type an Oracle element name to filter.
        </p>
      )}
      {filterOptions && !elements.length && (
        <p className="text-xs text-slate-400">No Oracle elements available yet — type the element name to filter.</p>
      )}
    </div>
  );
}

function formatMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(v);
}

function formatHours(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    : String(v);
}
